import React, { ReactElement, useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, ListGroup, Fade } from 'react-bootstrap'
import styles from './styles/search-bar.module.scss'
import { useDebounce } from 'hooks/use-debounce'
import { isNullOrUndefined } from 'is-what'
import { Player } from 'types/global'
import WTNSpinner from 'components/generic/WTNSpinner'
import { createFullUrl } from 'constants/global'
import classNames from 'classnames'
import { ReactComponent as ClearSearchIcon } from 'images/icons/icn_delete.svg'

const normalizeText = (text: string) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '') || ''

type SearchInputSetter = (searchInput: string) => void

interface SearchBarContext {
  searchInput: string
  setSearchInput: SearchInputSetter
}
export interface SearchBarProps {
  shouldSearchOnLoad?: boolean
  initialSearchString?: string
  redirectOnViewAll?: boolean
  viewAllEnabled?: boolean
  value?: string
  onFinishedTyping: (text: string, context: SearchBarContext) => void
  onChange?: (text: string, context: SearchBarContext) => void
  onFocus?: () => void
  onBlur?: () => void
  onEnterKey?: (selected: Player, context: SearchBarContext) => void
  onSearch?: (text?: string) => void
  onClearInput?: () => void
  suggestions?: Player[]
  renderSuggestion?: (suggestion: Player, context: SearchBarContext) => ReactElement
  suggestionType?: string
  suggestionTypes?: string
  noSuggestionsExtraInfo?: string
  loading: boolean
  minCharsToGetSuggestions?: number
  classes?: { suggestionsWrapper?: string; inputWrapper?: string; input?: string }
  inputId?: string
}

interface SearchSuggestionProps {
  redirectOnViewAll: boolean | undefined
  searchText: string | undefined
  viewAllEnabled?: boolean
  showSuggestions?: boolean
  loading?: boolean
  suggestions?: Player[]
  renderSuggestion?: (id: Player) => ReactElement
  suggestionType: string
  suggestionTypes: string
  idSelected: string
  classSelected: string
  onSearch?: () => void
  onFocus?: () => void
  onBlur?: () => void
  noSuggestionsExtraInfo?: string
}

const LoadingListGroupItem = React.memo(() => (
  <div className={styles.spinnerWrapper}>
    <WTNSpinner width={60} />
  </div>
))

const ViewAllListItem: React.FC<{
  redirectOnViewAll: boolean | undefined
  searchText: string | undefined
  suggestionTypes: string
  onSearch?: () => void
  className?: string
}> = ({ redirectOnViewAll, searchText, suggestionTypes, onSearch, className = 'a' }) => {
  const { t: tSearchBar } = useTranslation('strSearchBar')

  const handleClick = () => {
    if (redirectOnViewAll) {
      window.location.href = createFullUrl(
        'PLAYER_SEARCH',
        searchText ? { queryParams: { search: searchText } } : undefined
      )
    } else {
      onSearch?.()
    }
  }
  return (
    <div
      onClick={() => handleClick()}
      className={classNames(styles.allPlayers, { [className]: className })}
    >
      <span>{tSearchBar('viewAll', { type: suggestionTypes })}</span>
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='24'
        height='24'
        fill='none'
        viewBox='0 0 24 24'
      >
        <path
          stroke='#DC3741'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2'
          d='M9.9 7.5l4.8 4.5-4.8 4.5'
        />
      </svg>
    </div>
  )
}

const NoSuggestionsFound = React.memo<{
  suggestionTypes: string
  extraInfo?: string
}>(({ suggestionTypes, extraInfo }) => {
  const { t: tSearchBar } = useTranslation('strSearchBar')

  return (
    <ListGroup.Item className={`border-0 ${styles.noSuggestionsFound}`}>
      <span className={styles.placeholder}>
        {tSearchBar('notFound', { type: suggestionTypes || '' })}
      </span>
      {extraInfo && (
        <small className={styles.extraInfoText} dangerouslySetInnerHTML={{ __html: extraInfo }} />
      )}
    </ListGroup.Item>
  )
})

const SearchSuggestions: React.FC<SearchSuggestionProps> = ({
  redirectOnViewAll,
  searchText,
  viewAllEnabled,
  showSuggestions,
  loading,
  suggestions,
  classSelected,
  idSelected,
  renderSuggestion,
  suggestionType,
  suggestionTypes,
  onSearch,
  onFocus,
  onBlur,
  noSuggestionsExtraInfo,
}) => {
  return (
    <Fade unmountOnExit in={isNullOrUndefined(showSuggestions) || showSuggestions}>
      <ul className={styles.searchSuggestionContainer} {...{ onFocus, onBlur, onClick: onBlur }}>
        {loading && (
          <li>
            <LoadingListGroupItem />
          </li>
        )}
        {!loading &&
          suggestions &&
          suggestions.length === 0 && [
            <NoSuggestionsFound
              {...{ suggestionTypes, extraInfo: noSuggestionsExtraInfo }}
              key='noneFound'
            />,
          ]}
        {!loading && suggestions && suggestions.length > 0 && (
          <>
            {onSearch && viewAllEnabled && (
              <ViewAllListItem
                redirectOnViewAll={redirectOnViewAll}
                searchText={searchText}
                key='viewAll'
                className={classNames({ [classSelected]: idSelected === 'viewAll' })}
                {...{ suggestionTypes, onSearch }}
              />
            )}
            {suggestions.map(suggestion => {
              return renderSuggestion ? (
                <li
                  key={suggestion.id}
                  className={idSelected === suggestion.id ? classSelected : ''}
                >
                  {renderSuggestion(suggestion)}
                </li>
              ) : undefined
            })}
          </>
        )}
      </ul>
    </Fade>
  )
}

export const SearchButton = React.memo<{ onClick: () => void; disabled?: boolean }>(buttonProps => (
  <Button variant='success' className={styles.compareBtn} {...buttonProps}>
    compare
  </Button>
))

const SearchBar: React.FC<SearchBarProps> = ({
  initialSearchString = '',
  redirectOnViewAll,
  viewAllEnabled = true,
  value,
  onFinishedTyping,
  onChange,
  onFocus,
  onBlur,
  onEnterKey,
  onSearch,
  onClearInput,
  suggestions,
  renderSuggestion,
  suggestionType = '',
  suggestionTypes = '',
  noSuggestionsExtraInfo = '',
  loading,
  children,
  minCharsToGetSuggestions = 0,
  classes = {},
  inputId,
  shouldSearchOnLoad = false,
}) => {
  const { t: tSearchBar } = useTranslation('strSearchBar')
  const [inputFocused, setInputFocused] = useState<boolean>(false)
  const [searchInput, setSearchInput] = useState(initialSearchString ?? '')
  const [debouncedSearchText, isDebouncing] = useDebounce(searchInput, 250)
  const showSuggestions =
    !!renderSuggestion &&
    !!searchInput &&
    searchInput.trim().length >= minCharsToGetSuggestions &&
    inputFocused
  const inputRef = useRef<HTMLInputElement>(null)

  const [results, setResults] = useState<{ id: string }[]>(
    viewAllEnabled ? [{ id: 'viewAll' }] : []
  )
  const [selectedIndex, setSelectedIndex] = useState<number>(0)

  useEffect(() => {
    if (debouncedSearchText.length && debouncedSearchText.length >= minCharsToGetSuggestions) {
      onFinishedTyping(debouncedSearchText, { searchInput, setSearchInput })
    }
  }, [debouncedSearchText, onFinishedTyping, minCharsToGetSuggestions])

  useEffect(() => {
    if (shouldSearchOnLoad) onSearch?.(initialSearchString)
  }, [initialSearchString, onSearch, shouldSearchOnLoad])

  useEffect(() => {
    if (suggestions && suggestions.length) {
      setResults(viewAllEnabled ? [{ id: 'viewAll' }, ...suggestions] : suggestions)
      setSelectedIndex(0)
    }
  }, [suggestions, viewAllEnabled])

  return (
    <div className={classNames(styles.inputSuggestionWrapper, classes?.suggestionsWrapper)}>
      <div className={classNames(styles.searchInputWrapper, classes?.inputWrapper)}>
        <input
          id={inputId}
          ref={inputRef}
          value={value || searchInput}
          onFocus={() => {
            setInputFocused(true)
            onFocus && onFocus()
          }}
          onBlur={() => {
            setInputFocused(false)
            onBlur && onBlur()
          }}
          onChange={evt => {
            const str = normalizeText(evt.target.value)
            onChange && onChange(str, { searchInput, setSearchInput })
            setSearchInput(str)
          }}
          onKeyDown={evt => {
            if (evt.key === 'Enter') {
              let target = evt.target as HTMLInputElement
              if (results[selectedIndex]?.id && results[selectedIndex].id !== 'viewAll') {
                onEnterKey?.(results[selectedIndex], { searchInput, setSearchInput })
              } else if (results[selectedIndex]?.id && results[selectedIndex].id === 'viewAll') {
                onSearch?.(normalizeText(target.value))
              }
              inputRef && inputRef.current && inputRef.current.blur()
            } else if (evt.key === 'ArrowDown') {
              evt.preventDefault()
              setSelectedIndex(prev => {
                return prev >= results.length - 1 ? results.length - 1 : prev + 1
              })
            } else if (evt.key === 'ArrowUp') {
              evt.preventDefault()
              setSelectedIndex(prev => {
                return prev <= 1 ? 0 : prev - 1
              })
            }
          }}
          className={classNames(styles.searchInput, classes?.input)}
          placeholder={
            suggestionType !== ''
              ? tSearchBar('placeholder', { type: suggestionType })
              : tSearchBar('placeholderDefault')
          }
          data-testid='player-search-bar'
        />
        {searchInput && (
          <button
            className={styles.clearBtn}
            onClick={() => {
              setSearchInput('')
              onClearInput?.()
              inputRef.current?.focus()
            }}
          >
            <ClearSearchIcon />
          </button>
        )}
      </div>

      <SearchSuggestions
        searchText={debouncedSearchText}
        redirectOnViewAll={redirectOnViewAll}
        viewAllEnabled={viewAllEnabled}
        idSelected={results[selectedIndex]?.id || ''}
        classSelected={styles.selectedSuggestion}
        {...{
          showSuggestions,
          loading: loading || isDebouncing,
          suggestions,
          renderSuggestion:
            renderSuggestion &&
            (suggestion => renderSuggestion(suggestion, { searchInput, setSearchInput })),
          onSearch: () =>
            onSearch?.(!suggestions || suggestions.length === 0 ? '' : debouncedSearchText),
          suggestionType,
          suggestionTypes,
          onFocus: () => setInputFocused(true),
          onBlur: () => setInputFocused(false),
        }}
        noSuggestionsExtraInfo={noSuggestionsExtraInfo}
      />
      <div>{children}</div>
    </div>
  )
}

export default SearchBar
